import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Search, ArrowUpDown, XCircle, Trash2, CheckCircle2, BellRing, Route, Save, X, UserCog, User as UserIcon, Bus } from "lucide-react";
import {
  fetchDispensaries,
  fetchFacets,
  createSnapshot,
  fetchUsers,
  bulkAssignDispensaries,
  api as apiClient,
} from "../lib/api";
import { STAGES, stageMeta } from "../lib/stages";
import { RunScanButton } from "../components/RunScanButton";
import { ExportMenu } from "../components/ExportMenu";
import { ImportDccDialog } from "../components/ImportDccDialog";
import { EmailRouteButton } from "../components/EmailRouteButton";
import { SingleSelectCombobox } from "../components/SingleSelectCombobox";
import { MyOpenTasksCard } from "../components/MyOpenTasksCard";
import { CreateBusTastingDialog } from "../components/CreateBusTastingDialog";
import { RequestDispensaryPanel } from "../components/RequestDispensaryPanel";
import { PriorityChipStack } from "../components/PriorityChipStack";
import { fetchPartners } from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { buildGoogleMapsRouteUrl } from "../lib/routes";
import { useDepot } from "../lib/useDepot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { value: "name", label: "Name (A→Z)" },
  { value: "recently_updated", label: "Recently updated" },
  { value: "assignee_asc", label: "Assignee (A→Z, unassigned last)" },
  { value: "assignee_desc", label: "Assignee (Z→A, unassigned first)" },
  { value: "newest_opening", label: "Newest opening" },
  { value: "oldest_opening", label: "Oldest opening" },
];

/** Inline stage chip with dropdown for the list page. Wrapping it in a
 *  div with `data-stage-chip-stop` lets the surrounding row-link detect
 *  that the click was on the chip and skip navigation. */
function StageChip({ value, onChange }) {
  const meta = stageMeta(value);
  return (
    <div
      data-stage-chip-stop
      onClick={(e) => e.preventDefault()}
      className="inline-block"
    >
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className={`h-6 px-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border ${meta.border} ${meta.bg} ${meta.color} w-auto`}
          title="Click to move stage"
        >
          <SelectValue>{meta.label}</SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          {STAGES.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              <span className={s.color}>{s.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function Dispensaries() {
  const { role, user: me } = useAuth();
  const canScan = role === "admin";
  const [items, setItems] = useState(null);
  const [facets, setFacets] = useState({ cities: [], counties: [] });
  const [q, setQ] = useState("");
  const [city, setCity] = useState("ALL");
  const [county, setCounty] = useState("ALL");
  const [confidence, setConfidence] = useState("ALL");
  const [assigneeFilter, setAssigneeFilter] = useState("ALL"); // "ALL" | "unassigned" | user_id
  const [stageFilter, setStageFilter] = useState("ALL");
  const [partnerFilter, setPartnerFilter] = useState("ALL"); // "ALL" | partner_id
  const [sort, setSort] = useState("name");
  const [view, setView] = useState("active"); // "active" | "closed"
  const [closedCount, setClosedCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [savingList, setSavingList] = useState(false);
  const [bookendDepot, setBookendDepot] = useState(() => {
    try {
      return localStorage.getItem("jacktrack_bookend_depot") === "1";
    } catch {
      return false;
    }
  });
  const depot = useDepot();
  const navigate = useNavigate();

  // Admin/dispatcher need the user roster for the Assignee filter
  // dropdown + the bulk-assign action. Sales reps don't see those
  // controls, so skip the fetch for them (also avoids a 403).
  const canManageAssignment = role === "admin" || role === "dispatcher";
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!canManageAssignment) return;
    fetchUsers()
      .then((list) =>
        setUsers(
          (list || []).filter((u) =>
            ["sales", "admin", "dispatcher"].includes(u.role)
          )
        )
      )
      .catch(() => setUsers([]));
  }, [canManageAssignment]);

  // Map Contact.id → user display name so the table can render assignees.
  // Built from the users roster (User.email → Contact email match).
  const [contactsByEmail, setContactsByEmail] = useState({});
  useEffect(() => {
    if (!canManageAssignment) return;
    apiClient
      .get("/contacts")
      .then((r) => {
        const map = {};
        for (const c of r.data || []) {
          if (c.email) map[c.email.toLowerCase()] = c;
        }
        setContactsByEmail(map);
      })
      .catch(() => setContactsByEmail({}));
  }, [canManageAssignment]);

  // Sales reps don't have permission to list all contacts, but they need
  // to render the assignee chip on rows assigned to themselves (otherwise
  // their own accounts look "Unassigned" — confusing). Look up *just*
  // their own Contact row so we can resolve `assigned_to_id === me.contact_id`.
  const [myContactId, setMyContactId] = useState(null);
  useEffect(() => {
    if (canManageAssignment) return; // admins/dispatchers already have everything
    if (!me?.email) return;
    apiClient
      .get("/auth/me/contact")
      .then((r) => setMyContactId(r.data?.id || null))
      .catch(() => setMyContactId(null));
  }, [canManageAssignment, me?.email]);

  // Reverse map: contact_id → user (for assignee column rendering).
  const userByContactId = useMemo(() => {
    const out = {};
    for (const u of users) {
      const c = contactsByEmail[(u.email || "").toLowerCase()];
      if (c) out[c.id] = u;
    }
    return out;
  }, [users, contactsByEmail]);

  // Partner map (id → {name, brand_color}) for the priority chip stack
  // rendered on each row. Fetched once on page load for everyone —
  // active partners are visible to all authenticated users, and we only
  // keep the fields the chip needs.
  const [partnerMap, setPartnerMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    fetchPartners()
      .then((list) => {
        if (cancelled) return;
        const map = {};
        for (const p of list || []) {
          map[p.id] = { name: p.name, brand_color: p.brand_color };
        }
        setPartnerMap(map);
      })
      .catch(() => setPartnerMap({}));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("jacktrack_bookend_depot", bookendDepot ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [bookendDepot]);

  const load = async () => {
    const params = {
      q: q || undefined,
      city: city !== "ALL" ? city : undefined,
      county: county !== "ALL" ? county : undefined,
      confidence: confidence !== "ALL" ? confidence : undefined,
      account_stage: stageFilter !== "ALL" ? stageFilter : undefined,
      assigned_user_id:
        assigneeFilter !== "ALL" ? assigneeFilter : undefined,
      priority_partner_id:
        partnerFilter !== "ALL" ? partnerFilter : undefined,
      sort,
      // Lift the page-size cap: the user expects the full ~1,200-record
      // DCC list, not the legacy 500-default. Backend cap is 5000.
      limit: 5000,
      // closed view: ask backend for ALL records, filter to closed only client-side
      include_closed: view === "closed",
    };
    const data = await fetchDispensaries(params);
    if (view === "closed") {
      setItems(data.filter((d) => d.status === "closed"));
    } else {
      setItems(data);
    }
  };

  const refreshClosedCount = async () => {
    const all = await fetchDispensaries({ include_closed: true, limit: 2000 });
    setClosedCount(all.filter((d) => d.status === "closed").length);
  };

  const handleDelete = async (d) => {
    if (
      !window.confirm(
        `Permanently delete "${d.nickname || d.name}" from JackTrack? This cannot be undone.`
      )
    )
      return;
    try {
      await apiClient.delete(`/dispensaries/${d.id}`);
      toast.success(`${d.nickname || d.name} deleted`);
      load();
      refreshClosedCount();
    } catch (e) {
      toast.error("Could not delete dispensary");
    }
  };

  useEffect(() => {
    fetchFacets().then(setFacets);
    refreshClosedCount();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, city, county, confidence, stageFilter, assigneeFilter, partnerFilter, sort, view]);

  const total = useMemo(() => (items ? items.length : 0), [items]);

  // Build city↔county lookup maps from the facets pairs.
  //   cityToCounty[city]      = most-common county for that city
  //   countyToCities[county]  = sorted unique cities in that county
  const { cityToCounty, countyToCities } = useMemo(() => {
    const cToCntyCounts = {}; // city -> {county: count}
    const cntyToCSet = {};   // county -> Set(cities)
    (facets.pairs || []).forEach(({ city, county, count }) => {
      if (city && county) {
        cToCntyCounts[city] = cToCntyCounts[city] || {};
        cToCntyCounts[city][county] =
          (cToCntyCounts[city][county] || 0) + (count || 1);
        cntyToCSet[county] = cntyToCSet[county] || new Set();
        cntyToCSet[county].add(city);
      }
    });
    const cityToCounty = {};
    Object.entries(cToCntyCounts).forEach(([city, counts]) => {
      cityToCounty[city] = Object.entries(counts).sort(
        (a, b) => b[1] - a[1]
      )[0][0];
    });
    const countyToCities = {};
    Object.entries(cntyToCSet).forEach(([cnty, set]) => {
      countyToCities[cnty] = Array.from(set).sort();
    });
    return { cityToCounty, countyToCities };
  }, [facets.pairs]);

  // City options narrow to the selected county when one is picked.
  const cityOptions = useMemo(() => {
    if (county !== "ALL" && countyToCities[county]) {
      return countyToCities[county];
    }
    return facets.cities || [];
  }, [county, countyToCities, facets.cities]);

  // Linked filter handlers — sentinel "ALL" maps to combobox `null`.
  const handleCityChange = (next) => {
    const v = next || "ALL";
    setCity(v);
    if (v !== "ALL") {
      const matched = cityToCounty[v];
      if (matched && county !== matched) setCounty(matched);
    }
  };
  const handleCountyChange = (next) => {
    const v = next || "ALL";
    setCounty(v);
    // If the currently-picked city no longer belongs to the chosen
    // county, drop it back to "All cities".
    if (v !== "ALL" && city !== "ALL" && cityToCounty[city] !== v) {
      setCity("ALL");
    }
  };

  // Selection helpers — only operate on records currently visible.
  const selectedRecords = useMemo(
    () => (items || []).filter((d) => selectedIds.includes(d.id)),
    [items, selectedIds]
  );
  const allVisibleSelected =
    items && items.length > 0 && items.every((d) => selectedIds.includes(d.id));

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !items.some((d) => d.id === id))
      );
    } else {
      const newIds = items.map((d) => d.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...newIds])));
    }
  };
  const clearSelection = () => setSelectedIds([]);

  // Virtualized scroll container — renders only the rows currently in
  // view (plus a small overscan buffer) so the page stays snappy even
  // with the full ~1,200 DCC dispensary list loaded into memory.
  const scrollParentRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: items?.length || 0,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => 56,        // px — matches `py-3` + text height
    overscan: 8,                   // render 8 extra rows above/below
  });

  const planSelectedRoute = () => {
    const withCoords = selectedRecords.filter(
      (d) => typeof d.latitude === "number" && typeof d.longitude === "number"
    );
    const useDepotBookend = bookendDepot && depot;
    if (!useDepotBookend && withCoords.length < 2) {
      toast.error("Need at least 2 stops with GPS coords, or enable depot bookend");
      return;
    }
    if (useDepotBookend && withCoords.length < 1) {
      toast.error("Pick at least 1 stop");
      return;
    }
    if (withCoords.length > 9) {
      toast.info("Google Maps caps free routes at 9 stops", {
        description:
          "Saving these as a lead list opens 'View on map' which handles >9 stops via the route panel.",
      });
    }
    const url = buildGoogleMapsRouteUrl(withCoords, {
      depot: useDepotBookend ? depot : null,
    });
    if (url) window.open(url, "_blank");
  };

  const saveSelectedAsLeadList = async () => {
    if (selectedRecords.length < 1) return;
    const title = window.prompt(
      "Name this lead list / route",
      `Hand-picked · ${selectedRecords.length} stops · ${new Date()
        .toISOString()
        .slice(0, 10)}`
    );
    if (!title) return;
    try {
      setSavingList(true);
      const snap = await createSnapshot({
        title: title.trim(),
        subtitle: `${selectedRecords.length} hand-picked dispensaries`,
        filters: {
          dispensary_ids: selectedRecords.map((d) => d.id),
          limit: selectedRecords.length,
          sort: "name",
          include_closed: view === "closed",
        },
      });
      toast.success("Lead list saved", {
        description: `${snap.total_count} stops · opening Lead Generator…`,
      });
      navigate(`/snapshots/${snap.id}`);
    } catch (e) {
      toast.error("Could not save lead list", { description: String(e) });
    } finally {
      setSavingList(false);
    }
  };

  // Admin/dispatcher bulk-assign the selected dispensaries to one rep.
  const [assigningTo, setAssigningTo] = useState(false);
  // Bulk-create bus tasting dialog (admin/dispatcher only)
  const [busTastingOpen, setBusTastingOpen] = useState(false);
  const assignSelectedToUser = async (userId) => {
    if (selectedIds.length < 1) return;
    const label =
      userId === "unassigned"
        ? "Unassigned"
        : (users.find((u) => u.user_id === userId)?.name ||
           users.find((u) => u.user_id === userId)?.email ||
           "selected rep");
    if (
      !window.confirm(
        `Reassign ${selectedIds.length} dispensary${selectedIds.length === 1 ? "" : "ies"} to ${label}?`
      )
    ) {
      return;
    }
    try {
      setAssigningTo(true);
      const res = await bulkAssignDispensaries(selectedIds, userId);
      toast.success(
        userId === "unassigned"
          ? `Cleared assignment from ${res.modified} dispensar${res.modified === 1 ? "y" : "ies"}`
          : `${res.modified} dispensar${res.modified === 1 ? "y" : "ies"} assigned to ${res.user_name || label}`
      );
      clearSelection();
      load();
    } catch (e) {
      toast.error("Bulk assign failed", {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setAssigningTo(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-5 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Dispensaries</h2>
          <p className="text-sm text-zinc-500 mt-1">
            <span data-testid="dispensaries-count" className="font-mono text-zinc-300">
              {total}
            </span>{" "}
            {view === "closed" ? "closed records — review and clean up." : "active records · Filter, sort, and explore evidence."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canScan && (
            <>
              <ImportDccDialog onImported={() => { load(); refreshClosedCount(); }} />
              <ExportMenu />
              <RunScanButton onComplete={() => { load(); refreshClosedCount(); }} />
            </>
          )}
        </div>
      </div>

      {/* My Open Tasks — sales reps see their personal queue front and center
          since this is their landing page. Other roles can ignore. */}
      {role === "sales" && <MyOpenTasksCard limit={6} />}

      {/* Active / Closed / Request tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button
          data-testid="tab-active"
          onClick={() => setView("active")}
          className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
            view === "active"
              ? "text-zinc-50"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          Active
          {view === "active" && (
            <span className="absolute left-0 right-0 -bottom-px h-px bg-emerald-400" />
          )}
        </button>
        <button
          data-testid="tab-closed"
          onClick={() => setView("closed")}
          className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
            view === "closed"
              ? "text-zinc-50"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <XCircle className="w-3.5 h-3.5 text-rose-400" />
          Closed
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400">
            {closedCount}
          </span>
          {view === "closed" && (
            <span className="absolute left-0 right-0 -bottom-px h-px bg-rose-400" />
          )}
        </button>
        {(role === "sales" || role === "lead_host" || role === "host") && (
          <button
            data-testid="tab-request"
            onClick={() => setView("request")}
            className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
              view === "request"
                ? "text-zinc-50"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <UserCog className="w-3.5 h-3.5 text-sky-400" />
            Request a dispensary
            {view === "request" && (
              <span className="absolute left-0 right-0 -bottom-px h-px bg-sky-400" />
            )}
          </button>
        )}
      </div>

      {view === "request" ? (
        <RequestDispensaryPanel />
      ) : (
        <>
      <div className="panel p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-3 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="search-input"
            placeholder={
              canManageAssignment
                ? "Search by name, license, or address…"
                : "Search by name…"
            }
            title={
              canManageAssignment
                ? "Searches dispensary name, DCC license number (paste C10-0000123-LIC or just digits), or street address."
                : undefined
            }
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-zinc-950 border-zinc-800 focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500/50 text-zinc-100"
          />
        </div>
        <div className="md:col-span-2">
          <SingleSelectCombobox
            value={city === "ALL" ? null : city}
            onChange={handleCityChange}
            options={cityOptions}
            allLabel={
              county !== "ALL" ? `All cities in ${county}` : "All cities"
            }
            placeholder="Type a city…"
            testId="city-filter"
          />
        </div>
        <div className="md:col-span-2">
          <SingleSelectCombobox
            value={county === "ALL" ? null : county}
            onChange={handleCountyChange}
            options={facets.counties || []}
            allLabel="All counties"
            placeholder="Type a county…"
            testId="county-filter"
          />
        </div>
        <div className="md:col-span-2">
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger
              data-testid="stage-filter"
              className="bg-zinc-950 border-zinc-800 text-zinc-100"
            >
              <SelectValue placeholder="Stage" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem value="ALL">All stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  <span className={s.color}>{s.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          {canManageAssignment ? (
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger
                data-testid="assignee-filter"
                className="bg-zinc-950 border-zinc-800 text-zinc-100"
                title="Filter by sales rep"
              >
                <span className="inline-flex items-center gap-1.5 truncate">
                  <UserCog className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <SelectValue placeholder="All reps" />
                </span>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="ALL">All reps</SelectItem>
                <SelectItem value="unassigned">
                  <span className="text-amber-300">Unassigned</span>
                </SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
        <div className="md:col-span-1">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              data-testid="sort-select"
              className="bg-zinc-950 border-zinc-800 text-zinc-100"
            >
              <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Partner priority filter — silent until any Partner exists. */}
      {Object.keys(partnerMap).length > 0 && (
        <div
          data-testid="partner-filter-strip"
          className="panel p-2 flex items-center gap-2 flex-wrap"
        >
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 px-1">
            Priority for
          </span>
          <button
            data-testid="partner-filter-all"
            onClick={() => setPartnerFilter("ALL")}
            className={`text-[11px] font-mono px-2.5 py-1 rounded border transition-colors ${
              partnerFilter === "ALL"
                ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
                : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            All
          </button>
          {Object.entries(partnerMap).map(([pid, p]) => (
            <button
              key={pid}
              data-testid={`partner-filter-${pid}`}
              onClick={() => setPartnerFilter(pid === partnerFilter ? "ALL" : pid)}
              className={`inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded border transition-colors ${
                partnerFilter === pid
                  ? "border-zinc-100 bg-zinc-800 text-zinc-50"
                  : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
              }`}
              title={p.name}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: p.brand_color || "#64748b" }}
              />
              <span className="truncate max-w-[120px]">{p.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="panel overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono border-b border-zinc-800 bg-zinc-950/80 backdrop-blur items-center sticky top-0 z-10">
          <div className="col-span-1 flex items-center">
            <Checkbox
              data-testid="select-all-visible"
              checked={!!allVisibleSelected}
              onCheckedChange={toggleAllVisible}
              aria-label="Select all visible"
            />
          </div>
          <div className="col-span-3">Dispensary</div>
          <div className="col-span-2">City</div>
          <div className="col-span-2">County</div>
          <div className="col-span-2">Assigned to</div>
          <div className={view === "closed" ? "col-span-1" : "col-span-2"}>
            Stage
          </div>
          {view === "closed" && (
            <div className="col-span-1 text-right">Action</div>
          )}
        </div>
        <div
          ref={scrollParentRef}
          data-testid="dispensary-list"
          className="max-h-[calc(100vh-340px)] overflow-y-auto overscroll-contain"
        >
          {items ? (
            items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-zinc-500">
                {view === "closed"
                  ? "No closed dispensaries — your active list is clean."
                  : "No dispensaries match these filters."}
              </div>
            ) : (
              // Virtualized list — only the visible window is actually
              // rendered to the DOM. The outer div claims the full
              // scroll height so the scrollbar behaves naturally; each
              // row is absolutely-positioned by the virtualizer.
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((vRow) => {
                  const d = items[vRow.index];
                  return (
                    <div
                      key={d.id}
                      data-testid={`dispensary-row-${d.id}`}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        transform: `translateY(${vRow.start}px)`,
                      }}
                      className={`grid grid-cols-12 gap-3 px-4 py-3 text-sm border-b border-zinc-900 hover:bg-zinc-800/30 transition-colors duration-150 items-center ${
                        selectedIds.includes(d.id) ? "bg-emerald-500/[0.03]" : ""
                      }`}
                    >
                      <div className="col-span-1 flex items-center">
                        <Checkbox
                          data-testid={`select-row-${d.id}`}
                          checked={selectedIds.includes(d.id)}
                          onCheckedChange={() => toggleOne(d.id)}
                          aria-label={`Select ${d.nickname || d.name}`}
                        />
                      </div>
                      <Link
                        to={`/dispensaries/${d.id}`}
                        className="col-span-3 min-w-0"
                      >
                        <div className="text-zinc-50 font-medium truncate flex items-center gap-2">
                          <span title={d.nickname ? `Licensed as ${d.name}` : undefined}>
                            {d.nickname || d.name}
                          </span>
                          {d.priority_partner_ids?.length > 0 && (
                            <PriorityChipStack
                              partner_ids={d.priority_partner_ids}
                              partnerMap={partnerMap}
                              dataTestId={`priority-chips-${d.id}`}
                            />
                          )}
                          {d.status === "closed" && (
                            <span
                              data-testid={`closed-badge-${d.id}`}
                              className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-rose-300 bg-rose-400/10 border border-rose-400/20 px-1.5 py-0.5 rounded"
                            >
                              <XCircle className="w-2.5 h-2.5" />
                              Closed
                            </span>
                          )}
                          {d.dcc_license_alert && d.status !== "closed" && (
                            <span
                              data-testid={`license-alert-${d.id}`}
                              className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-rose-300 bg-rose-400/10 border border-rose-400/20 px-1.5 py-0.5 rounded"
                              title={d.dcc_license_alert}
                            >
                              <BellRing className="w-2.5 h-2.5" />
                              Lic. alert
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">
                          {d.address}
                        </div>
                      </Link>
                      <Link
                        to={`/dispensaries/${d.id}`}
                        className="col-span-2 text-zinc-300 truncate"
                      >
                        {d.city || "—"}
                      </Link>
                      <Link
                        to={`/dispensaries/${d.id}`}
                        className="col-span-2 text-zinc-400 truncate"
                      >
                        {d.county || "—"}
                      </Link>
                      <Link
                        to={`/dispensaries/${d.id}`}
                        className="col-span-2 truncate"
                      >
                        {(() => {
                          const u = d.assigned_to_id ? userByContactId[d.assigned_to_id] : null;
                          if (u) {
                            return (
                              <span className="inline-flex items-center gap-1.5 text-xs text-indigo-300">
                                <UserIcon className="w-3 h-3" />
                                <span className="truncate">{u.name || u.email}</span>
                              </span>
                            );
                          }
                          // Sales reps don't load the users roster, but if
                          // this row is assigned to THEM, render their own
                          // name from the auth context.
                          if (
                            d.assigned_to_id &&
                            myContactId &&
                            d.assigned_to_id === myContactId
                          ) {
                            return (
                              <span className="inline-flex items-center gap-1.5 text-xs text-indigo-300">
                                <UserIcon className="w-3 h-3" />
                                <span className="truncate">{me?.name || me?.email}</span>
                              </span>
                            );
                          }
                          if (d.assigned_to_id) {
                            // Row is assigned to *someone else* but we
                            // can't resolve who (sales rep viewing a row
                            // they don't own — shouldn't happen, but be
                            // safe). Show neutral chip instead of
                            // "Unassigned" which is misleading.
                            return (
                              <span className="text-[11px] uppercase tracking-wider font-mono text-zinc-500">
                                Assigned
                              </span>
                            );
                          }
                          return (
                            <span className="text-[11px] uppercase tracking-wider font-mono text-amber-400/80">
                              Unassigned
                            </span>
                          );
                        })()}
                      </Link>
                      <Link
                        to={`/dispensaries/${d.id}`}
                        className={view === "closed" ? "col-span-1" : "col-span-2"}
                        onClick={(e) => {
                          // Don't navigate when the stage chip was clicked.
                          if (e.target.closest("[data-stage-chip-stop]")) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <StageChip
                          value={d.account_stage || "new"}
                          onChange={async (newStage) => {
                            try {
                              const updated = await apiClient.patch(
                                `/dispensaries/${d.id}`,
                                { account_stage: newStage }
                              );
                              setItems((prev) =>
                                (prev || []).map((row) =>
                                  row.id === d.id ? updated.data : row
                                )
                              );
                              toast.success(`Moved to ${stageMeta(newStage).label}`);
                            } catch {
                              toast.error("Could not change stage");
                            }
                          }}
                        />
                      </Link>
                      {view === "closed" && (
                        <div className="col-span-1 flex justify-end">
                          <button
                            data-testid={`delete-dispensary-${d.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDelete(d);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-rose-400 hover:bg-zinc-900 rounded transition-colors"
                            title="Delete permanently"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            [0, 1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton
                key={i}
                className="h-12 my-1 mx-2 rounded-md bg-zinc-900/40"
              />
            ))
          )}
        </div>
      </div>

      {/* Sticky multi-select action bar — designed for iPad/tablet too:
          two-row layout, wraps cleanly, respects iOS safe-area-inset.
          Admin/dispatcher: primary CTA is "Create Bus Tasting" (route
          planning happens in the next step on the Bus Tastings page).
          Sales reps: keeps "Plan route" as their primary action. */}
      {selectedIds.length > 0 && (
        <div
          data-testid="selection-action-bar"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          className="fixed bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 z-40 w-[min(96vw,1100px)] rounded-2xl border-2 border-emerald-500/60 bg-zinc-950/95 backdrop-blur-md shadow-[0_20px_60px_-10px_rgba(16,185,129,0.45)] ring-1 ring-emerald-500/20 fade-up"
        >
          {/* Header row — count + clear */}
          <div className="flex items-center justify-between gap-4 px-4 sm:px-5 py-2.5 border-b border-emerald-500/20 bg-emerald-500/5 rounded-t-2xl">
            <div className="flex items-center gap-2 text-emerald-200 min-w-0">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span className="text-sm font-semibold tracking-tight truncate">
                <span data-testid="selection-count" className="font-mono text-emerald-100">
                  {selectedIds.length}
                </span>
                {" "}dispensar{selectedIds.length === 1 ? "y" : "ies"} selected
              </span>
            </div>
            <button
              data-testid="selection-clear"
              onClick={clearSelection}
              className="shrink-0 text-zinc-500 hover:text-rose-300 inline-flex items-center gap-1 text-xs font-mono"
              aria-label="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
              clear
            </button>
          </div>

          {/* Primary actions row */}
          <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3">
            {canManageAssignment ? (
              <Button
                data-testid="selection-create-tasting"
                onClick={() => setBusTastingOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md h-10 px-4 text-sm font-medium"
              >
                <Bus className="w-4 h-4 mr-2" />
                Create Bus Event
              </Button>
            ) : (
              <Button
                data-testid="selection-plan-route"
                onClick={planSelectedRoute}
                className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md h-10 px-4 text-sm font-medium"
              >
                <Route className="w-4 h-4 mr-2" />
                {bookendDepot && depot ? "Plan depot route" : "Plan route"}
              </Button>
            )}

            {canManageAssignment && (
              <Select
                value=""
                onValueChange={(v) => v && assignSelectedToUser(v)}
                disabled={assigningTo}
              >
                <SelectTrigger
                  data-testid="selection-assign-rep"
                  className="bg-indigo-600 hover:bg-indigo-500 text-indigo-50 border-indigo-500 rounded-md h-10 px-4 text-sm font-medium w-auto inline-flex"
                  title="Assign these dispensaries to a sales rep"
                >
                  <span className="inline-flex items-center gap-2">
                    <UserCog className="w-4 h-4" />
                    {assigningTo ? "Assigning…" : "Assign to rep"}
                  </span>
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem value="unassigned">
                    <span className="text-amber-300 inline-flex items-center gap-2">
                      <X className="w-3 h-3" />
                      Clear assignment
                    </span>
                  </SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      <span className="inline-flex items-center gap-2">
                        <UserIcon className="w-3 h-3 text-indigo-300" />
                        {u.name || u.email}
                        <span className="text-[10px] uppercase font-mono text-zinc-500">
                          {u.role}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button
              data-testid="selection-save-list"
              variant="outline"
              onClick={saveSelectedAsLeadList}
              disabled={savingList}
              className="border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 rounded-md h-10 px-4 text-sm"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingList ? "Saving…" : "Save as list"}
            </Button>
          </div>

          {/* Secondary actions row — route/email helpers (kept available
              for both roles but de-emphasized). Bookend toggle inline. */}
          <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 pb-3 -mt-1">
            {canManageAssignment && (
              <Button
                data-testid="selection-plan-route"
                onClick={planSelectedRoute}
                variant="ghost"
                className="text-zinc-300 hover:text-zinc-100 hover:bg-zinc-900 h-8 px-2 text-xs"
              >
                <Route className="w-3.5 h-3.5 mr-1.5" />
                {bookendDepot && depot ? "Plan depot route" : "Plan route in Maps"}
              </Button>
            )}

            <EmailRouteButton
              testIdPrefix="dispensaries-email"
              getSubject={() => {
                const withCoords = selectedRecords.filter(
                  (d) => typeof d.latitude === "number" && typeof d.longitude === "number"
                );
                const n = withCoords.length;
                return bookendDepot && depot
                  ? `JackTrack route — Depot → ${n} stop${n === 1 ? "" : "s"} → Depot`
                  : `JackTrack ${n}-stop route`;
              }}
              getMessage={() => {
                const withCoords = selectedRecords.filter(
                  (d) => typeof d.latitude === "number" && typeof d.longitude === "number"
                );
                const url = buildGoogleMapsRouteUrl(withCoords, {
                  depot: bookendDepot && depot ? depot : null,
                });
                const n = withCoords.length;
                const label = bookendDepot && depot
                  ? `Depot → ${n} stop${n === 1 ? "" : "s"} → Depot`
                  : `${n}-stop`;
                return url
                  ? `${label} JackTrack route:\n${url}`
                  : `${selectedRecords.length} dispensaries selected`;
              }}
            />

            <label
              data-testid="selection-bookend-row"
              className="inline-flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer select-none px-2"
              title={
                depot
                  ? `Start & end at ${depot.address || "depot"}`
                  : "Set the depot in Settings to enable"
              }
            >
              <input
                data-testid="selection-bookend-toggle"
                type="checkbox"
                checked={bookendDepot}
                disabled={!depot}
                onChange={(e) => setBookendDepot(e.target.checked)}
                className="h-3.5 w-3.5 accent-emerald-500 disabled:opacity-40"
              />
              Bookend at depot
            </label>
          </div>
        </div>
      )}

      <CreateBusTastingDialog
        open={busTastingOpen}
        onOpenChange={setBusTastingOpen}
        dispensaries={selectedRecords}
        onCreated={() => {
          // Clear the multi-select after a successful batch so the bar
          // collapses and the dispatcher can move on cleanly.
          clearSelection();
        }}
      />
        </>
      )}
    </div>
  );
}
